class SwiperSlider extends HTMLElement {
    constructor() {
      super();
      this.slideContainer = this.querySelector(
        '[data-swiper]:not([data-swiper-thumbs])'
      );
      this.slides = this.querySelectorAll('[data-swiper-slide]');
      this.sliderOptions = this.parseOptions(this.dataset.options);
      this.activeIndex = 0;
      this.initialized = false;
      this.thumbnails = this.querySelector('[data-swiper-thumbs]');
      this.isVideoSlider = this.hasAttribute('video-slider');
  
      if (!this.hasAttribute('disable-auto-init')) {
        this.setupEventListeners();
      }
    }
  
    setupEventListeners() {
      if (this.slideContainer && this.slides.length > 0) {
        document.addEventListener('DOMContentLoaded', () =>
          this.initializeSwiper()
        );
        document.addEventListener('shopify:section:load', () =>
          this.initializeSwiper()
        );
        window.addEventListener(
          'resize',
          this.debouncedResize(() => this.updateSliderSize(), 300)
        );
      } else {
        console.warn('SwiperSlider - Slider or Slides Not Found');
      }
    }
  
    goToSlide(index) {
      if (!this.slider || index === undefined) return;
      this.slider.progressLoop
        ? this.slider.slideToLoop(index)
        : this.slider.slideTo(index);
    }
  
    parseOptions(options) {
      try {
        if (options) {
          const sanitizedOptions = options.replace(/'/g, '"');
          return JSON.parse(sanitizedOptions || '{}');
        }
        return {};
      } catch (error) {
        console.error('Error parsing options: ', error);
        return {};
      }
    }
  
    initializeSwiper() {
      if (window.Swiper && !this.initialized) {
        this.initialized = true;
        this.setThumbnails();
        this.slider = new Swiper(this.slideContainer, this.sliderOptions);
  
        this.slider.on('slideChange', () => {
          this.handleAutoplayVideos();
          this.updateSlideAttributes();
        });
  
        this.slider.on('reachEnd', () =>
          this.setAttribute('data-reached-end', '')
        );
        this.slider.on('reachBeginning', () =>
          this.setAttribute('data-reached-start', '')
        );
        this.slider.on('fromEdge', () => {
          this.removeAttribute('data-reached-end');
          this.removeAttribute('data-reached-start');
        });
  
        this.setAttribute('initialized', '');
        this.handleAutoplay();
        this.initializeSliderEventListeners();
        this.updateSlideAttributes();
  
        // Start observing attribute changes
        this.observeSlideChanges();
      } else if (!window.Swiper) {
        console.warn('SwiperSlider - Swiper.js not found');
      }
    }
  
    updateSliderSize() {
      this.slider?.updateSize();
    }
  
    updateSlideAttributes() {
      if (!this.slider) return;
  
      if (this.slider.isBeginning) {
        this.setAttribute('data-reached-start', '');
      } else {
        this.removeAttribute('data-reached-start');
      }
  
      if (this.slider.isEnd) {
        this.setAttribute('data-reached-end', '');
      } else {
        this.removeAttribute('data-reached-end');
      }
    }
  
    getSlide(index) {
      return this.slides[index];
    }
  
    handleSlideMedia(prevSlide, currentSlide) {
      if (currentSlide) {
        const currentSlideMedia = currentSlide.querySelector('video-media');
        if (currentSlideMedia) {
          currentSlideMedia.currentTime = 0;
          currentSlideMedia.play();
        }
      }
  
      if (prevSlide != undefined) {
        const prevSlideMedia = prevSlide.querySelector('video-media');
        if (prevSlideMedia) {
          prevSlideMedia.pause();
        }
      }
    }
  
    initializeSliderEventListeners() {
      if (this.slider) {
        this.slider.on('slideChange', () => {
          const prevSlide = this.slider.slides[this.slider.previousIndex];
          const currentSlide = this.slider.slides[this.slider.activeIndex];
  
          if (currentSlide) {
            currentSlide.removeAttribute('video-paused');
          }
  
          this.handleSlideMedia(prevSlide, currentSlide);
        });
      }
    }
  
    handleAutoplayVideos() {
      const activeSlide = this.getSlide(this.slider.activeIndex);
      if (!activeSlide) return;
  
      const autoplayVideoEl = activeSlide.querySelector('auto-play-video');
      if (!this.hasAttribute('data-autoplay-active-vids')) return;
      if (!autoplayVideoEl) return;
  
      if (typeof autoplayVideoEl.loadAndPlayVideo === 'function') {
        autoplayVideoEl.loadAndPlayVideo();
      } else {
        console.error('The element does not have a loadAndPlayVideo method.');
      }
    }
  
    handleAutoplay() {
      if (this.hasAttribute('swiper-mouse-sensitive')) {
        this.addEventListener('mouseenter', () => this.slider?.autoplay?.stop());
        this.addEventListener('mouseleave', () => this.slider?.autoplay?.start());
      }
  
      const mediaQuery = this.getAttribute('autoplay-disable-media');
      if (mediaQuery) {
        const mediaQueryList = window.matchMedia(`(max-width: ${mediaQuery})`);
        if (mediaQueryList.matches) {
          this.slider?.autoplay?.stop();
          Object.assign(this.slider.params, {
            autoplay: { disableOnInteraction: true },
            freeMode: { enabled: false },
            speed: 400,
          });
        }
      }
    }
  
    setThumbnails() {
      if (this.thumbnails) {
        const thumbOptions = this.parseOptions(
          this.thumbnails.dataset.swiperOptions
        );
        const thumbnailsSlider = new Swiper(this.thumbnails, thumbOptions);
        this.sliderOptions.thumbs = { swiper: thumbnailsSlider };
      }
    }
  
    debouncedResize(callback, delay) {
      let timeout;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(callback, delay);
      };
    }
  
    // Observe attribute changes and scroll to the corresponding slide
    observeSlideChanges() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            ['open', 'active', 'selected'].includes(mutation.attributeName)
          ) {
            // Ensure the attribute was just added, not removed
            if (mutation.oldValue === null) {
              const slide = mutation.target.closest('[data-swiper-slide]');
              if (slide) {
                const index = Array.from(this.slides).indexOf(slide);
                if (index !== -1) {
                  this.goToSlide(index);
                }
              }
            }
          }
        });
      });
  
      this.slides.forEach((slide) => {
        observer.observe(slide, {
          attributes: true,
          subtree: true, // Watch elements inside the slide
          attributeFilter: ['open', 'active', 'selected'],
          attributeOldValue: true, // Ensures we can detect if the attribute was just added
        });
      });
    }
  }

// Register custom elements
customElements.define('swiper-slider', SwiperSlider);
  